import React, {useEffect, useRef, useState} from 'react'
import {Util} from 'yougile-util'
import {keyboardHandler, noPropagate} from '@containers/hooks'
import {InputNew} from './input-new'
import {NumberInputNewProps} from './types'

const maxInterval = 100
const minInterval = 40
const lerpTime = 2_500
const firstTimeout = 700

const nextTimestamp = (start: number) =>
  Date.now() +
  Util.lerp(maxInterval, minInterval, (Date.now() - start) / lerpTime)

export const NumberInputNew = (props: NumberInputNewProps) => {
  const {
    value,
    onChange,
    step = 1,
    disabled,
    isPlusMinusVisible = true,
    className,
    inputClassName
  } = props
  const {min = Number.NEGATIVE_INFINITY, max = Number.POSITIVE_INFINITY} = props

  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const fn = (e: WheelEvent) => e.stopPropagation()
    ref.current?.addEventListener('wheel', fn, {passive: false})
    return () => ref.current?.removeEventListener('wheel', fn)
  }, [ref])

  const updateLockRef = useRef(false)
  const lockUpdate = () => (updateLockRef.current = true)
  const unlockUpdate = () => (updateLockRef.current = false)

  const firstFrameRef = useRef<ReturnType<typeof setTimeout>>()
  const frameRef = useRef<number>()

  const stopScheduled = (): void => {
    document.removeEventListener('mouseup', stopScheduled, {capture: true})
    unlockUpdate()
    firstFrameRef.current && clearTimeout(firstFrameRef.current)
    firstFrameRef.current = undefined
    frameRef.current && cancelAnimationFrame(frameRef.current)
    frameRef.current = undefined
  }

  useEffect(() => stopScheduled, [frameRef])

  const [localValue, setLocalValue] = useState(value)
  const [localText, setLocalText] = useState(() => localValue.toFixed(0))

  useEffect(() => {
    setLocalText(localValue.toFixed(0))
  }, [localValue])

  useEffect(() => {
    // Update local value only when a user is not editing the field
    // This may cause problems (?), maybe requires props.onChange call on blur
    !updateLockRef.current && setLocalValue(value)
  }, [updateLockRef, value])

  const addToLocal = (delta: number) =>
    setLocalValue(prev => {
      let newValue = prev + delta
      newValue = Math.max(Math.min(newValue, max), min)
      onChange(newValue)
      return newValue
    })

  const reschedule = (delta: number, start: number, timestamp: number) => {
    if (!frameRef.current) {
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = undefined

        if (Date.now() < timestamp) {
          return reschedule(delta, start, timestamp)
        }

        addToLocal(delta)
        reschedule(delta, start, nextTimestamp(start))
      })
    }
  }

  const schedule = (delta: number) => {
    if (!firstFrameRef.current) {
      document.addEventListener('mouseup', stopScheduled, {capture: true})
      lockUpdate()

      firstFrameRef.current = setTimeout(() => {
        addToLocal(delta)
        const now = Date.now()
        reschedule(delta, now, nextTimestamp(now))
      }, firstTimeout)
    }
  }

  const onStartIncrease = (e: React.MouseEvent) => {
    e.stopPropagation()
    addToLocal(step)
    schedule(step)
  }

  const onStartDecrease = (e: React.MouseEvent) => {
    e.stopPropagation()
    addToLocal(-step)
    schedule(-step)
  }

  const onInputChange = (text: string) => {
    let newVal = Number.parseInt(text)
    if (Number.isFinite(newVal)) {
      newVal = Math.round(newVal)
      newVal = Math.max(Math.min(newVal, max), min)
      onChange(newVal)
      setLocalValue(newVal)
      setLocalText(newVal.toFixed(0))
    } else {
      setLocalText(text)
    }
  }

  const onBlur = () => {
    unlockUpdate()
    const val = Number.parseInt(localText)
    if (!Number.isFinite(val)) {
      onInputChange('0')
    }
  }

  return (
    <InputNew
      ref={ref}
      type="number"
      className={className}
      inputClassName={inputClassName}
      value={localText}
      iconLeft={isPlusMinusVisible ? 'IconMinus' : undefined}
      iconRight={isPlusMinusVisible ? 'IconPlus' : undefined}
      onChange={onInputChange}
      onIconLeftMouseDown={onStartDecrease}
      onIconRightMouseDown={onStartIncrease}
      onIconClick={noPropagate}
      onFocus={lockUpdate}
      onBlur={onBlur}
      onKeyDown={keyboardHandler({
        escape: () => {
          ref.current?.blur()
          return true
        },
        anyKey: ({key}) => {
          if (key === '-' && localText === '0') {
            setLocalText('-')
          } else if (key === '+' && localText === '0') {
            setLocalText('+')
          }
        }
      })}
      disabled={disabled}
      step={step}
      min={min}
      max={max}
    />
  )
}
